import moment from 'moment-timezone';
import React, { FC, useState } from 'react';
import {
  FCOFields,
  FeatureServiceFCO,
  FeatureServiceFCOFields,
  FeatureViewFCO,
  FeatureViewFCOFields,
  OnlineStoreType,
} from '../../../core/types/fcoTypes';
import { CustomGraph } from '../../../feature/feature-views/graph-types';
import {
  FEATURE_SERVICE_GRAPHS,
  FEATURE_SERVICE_GRAPHS_FOR_ODFV_ONLY,
  FILTERABLE_PANEL_LABEL,
} from '../../../feature/graph-configuration';
import { useUserSettings } from '../../context/UserSettingsContext';
import {
  MonitoringDateRange,
  calculateStep,
  calculateUnit,
  getDefaultMonitoringDateRange,
} from '../../monitoring/DateRange-Dropdown-util';
import MonitoringGraphs from '../monitoring/MonitoringGraphs';
import { TitleText } from '../monitoring/StyledComponents';
import OnlineStoreLatency from './OnlineStoreLatency';
import ReliabilitySLO30Days from './ReliabilitySLO30Days';

// @tecton
import { FlexGroup, FlexItem } from '@lithos';
import { TimeComboDateRange, TimeComboOptionType } from '@tecton/ComponentRedesign/lib/TimeComboSelector';
import { TimeSelector } from '../../@tecton';

interface FeatureServiceDetailMonitoringProps {
  featureService: FeatureServiceFCO;
  featureViews: FeatureViewFCO[];
}

// This is in the Feature View Details page -> monitoring tab.
const FeatureServiceDetailMonitoring: FC<FeatureServiceDetailMonitoringProps> = ({ featureService, featureViews }) => {
  const { workspace } = useUserSettings();
  const defaultMonitoringDateRange: MonitoringDateRange = getDefaultMonitoringDateRange();
  const [monitoringDateRange, setMonitoringDateRange] = useState<MonitoringDateRange>(defaultMonitoringDateRange);
  const [selectedTimeComboOption, setSelectedTimeComboOption] = useState<TimeComboOptionType>('1Hour');
  const [dateRange, setDateRange] = useState<TimeComboDateRange>({
    startDate: moment().local().subtract(1, `hour`),
    endDate: moment().local(),
  });

  const relatedFvs = featureViews.filter((fv) => {
    return featureService[FeatureServiceFCOFields.ALL_FEATURE_VIEWS].includes(fv[FCOFields.ID]);
  });

  const hasDynamoStoreType = relatedFvs
    .map((fv) => {
      return fv[FeatureViewFCOFields.ONLINE_STORE_TYPE];
    })
    .includes(OnlineStoreType.DYNAMO);

  const filteredGraphData = FEATURE_SERVICE_GRAPHS.filter((graphRowData) => {
    // Hide get feature-bach-graph if it's Dynamo DB
    const filterForRedis = [FILTERABLE_PANEL_LABEL.GET_FEATURES_BATCH.toString()];
    const filterForDynamoDB = [FILTERABLE_PANEL_LABEL.FEATURE_SERVICE_DYNAMO_GRAPHS.toString()];

    // Remove FEATURE_SERVICE_DYNAMO_GRAPHS graphs for redesign since we are rendering it as custom
    if (graphRowData.panelLabel === FILTERABLE_PANEL_LABEL.FEATURE_SERVICE_DYNAMO_GRAPHS) {
      return false;
    }

    if (filterForRedis.includes(graphRowData.panelLabel)) {
      const hasRedis = relatedFvs
        .map((fv) => {
          return fv[FeatureViewFCOFields.ONLINE_STORE_TYPE];
        })
        .includes(OnlineStoreType.REDIS);

      // Hide get feature bach graph if it's Dynamo DB
      return hasRedis;
    }

    // Show dynamoDB if FS has it.
    if (filterForDynamoDB.includes(graphRowData.panelLabel)) {
      const hasDynamoStoreType = relatedFvs
        .map((fv) => {
          return fv[FeatureViewFCOFields.ONLINE_STORE_TYPE];
        })
        .includes(OnlineStoreType.DYNAMO);

      // Show get Dynamo DB graphs if some FV has it
      return hasDynamoStoreType;
    }

    if (graphRowData.panelLabel === FILTERABLE_PANEL_LABEL.TECTON_SERVING_CACHE) {
      return featureService[FeatureServiceFCOFields.IS_CACHING_ENABLED];
    }

    return true;
  });

  const customGraphRecord: Record<string, React.ReactNode> = {
    [CustomGraph.FEATURE_SERVICE_GLOBAL_SLO_RELIABILITY]: (
      <FlexItem grow={2}>
        {
          <ReliabilitySLO30Days
            featureServiceId={featureService[FCOFields.ID]}
            monitoringDateRange={monitoringDateRange}
          />
        }
      </FlexItem>
    ),
  };

  return (
    <>
      <FlexGroup direction="column" gapSize="4" padding="0" css={{ overflow: 'scroll' }}>
        <TimeSelector
          onSelection={(start, end) => {
            const step = calculateStep(moment(start), moment(end));
            const unit = calculateUnit(moment(start), moment(end));
            setMonitoringDateRange({
              startTime: () => start.valueOf(),
              endTime: () => end.valueOf(),
              requestId: `${start.valueOf()}${end.valueOf()}`,
              step,
              unit,
            });
          }}
        />

        <FlexItem grow={1} shrink={0}>
          <MonitoringGraphs
            monitoringDateRange={monitoringDateRange}
            graphsData={filteredGraphData}
            customGraphRecord={customGraphRecord}
            workspace={workspace ?? ''}
            fcoId={featureService[FCOFields.ID] ?? ''}
          />
        </FlexItem>
        {hasDynamoStoreType && (
          <>
            <FlexItem grow={1} shrink={0}>
              <FlexGroup gapSize={'2'} direction="column">
                <FlexItem>
                  <TitleText>Online Store Latency</TitleText>
                </FlexItem>
                <FlexItem>
                  <OnlineStoreLatency relatedFvs={relatedFvs} monitoringDateRange={monitoringDateRange} />
                </FlexItem>
              </FlexGroup>
            </FlexItem>
          </>
        )}
        <FlexItem grow={1} shrink={0}>
          <MonitoringGraphs
            monitoringDateRange={monitoringDateRange}
            graphsData={FEATURE_SERVICE_GRAPHS_FOR_ODFV_ONLY}
            customGraphRecord={customGraphRecord}
            workspace={workspace ?? ''}
            fcoId={featureService[FCOFields.ID] ?? ''}
          />
        </FlexItem>
      </FlexGroup>
    </>
  );
};

export default FeatureServiceDetailMonitoring;
